import React, { createContext, useState, useCallback, useEffect, useContext, useRef } from 'react';
import {
  notificationsApi,
  createNotificationEventSource,
  Notification,
  NotificationCategory,
} from '../api';
import { AuthContext } from './AuthContext';
import { usePageVisibility } from '../hooks/core';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  category: NotificationCategory;
  // Actions
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  setCategory: (category: NotificationCategory) => void;
  // Subscribe to global merge request changes (for board pages that show pending merge requests)
  subscribeToGlobalMergeChanges: (callback: () => void) => () => void;
}

const PAGE_SIZE = 20;

// eslint-disable-next-line react-refresh/only-export-components
export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const auth = useContext(AuthContext);
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const isVisible = usePageVisibility();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [category, setCategory] = useState<NotificationCategory>('all');
  const [offset, setOffset] = useState(0);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const globalMergeSubscribersRef = useRef<Set<() => void>>(new Set());
  const wasHiddenRef = useRef(false);

  // Subscribe to global merge request changes
  const subscribeToGlobalMergeChanges = useCallback((callback: () => void) => {
    globalMergeSubscribersRef.current.add(callback);
    return () => {
      globalMergeSubscribersRef.current.delete(callback);
    };
  }, []);

  // Notify all global merge subscribers
  const notifyGlobalMergeSubscribers = useCallback(() => {
    globalMergeSubscribersRef.current.forEach((callback) => callback());
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await notificationsApi.getUnreadCount();
      setUnreadCount(response.unreadCount);
    } catch (err) {
      console.error('[Notifications] Failed to fetch unread count:', err);
    }
  }, [isAuthenticated]);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (reset = false) => {
      if (!isAuthenticated) return;

      setIsLoading(true);
      setError(null);

      try {
        const newOffset = reset ? 0 : offset;
        const response = await notificationsApi.list({
          category: category === 'all' ? undefined : category,
          limit: PAGE_SIZE,
          offset: newOffset,
        });

        if (reset) {
          setNotifications(response.notifications);
          setOffset(PAGE_SIZE);
        } else {
          setNotifications((prev) => [...prev, ...response.notifications]);
          setOffset(newOffset + PAGE_SIZE);
        }

        setHasMore(response.notifications.length === PAGE_SIZE);
      } catch (err) {
        console.error('[Notifications] Failed to fetch notifications:', err);
        setError('Failed to load notifications');
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, category, offset]
  );

  // Refresh both notifications and unread count
  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(true), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    await fetchNotifications(false);
  }, [isLoading, hasMore, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await notificationsApi.markAsRead(notificationId);
        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('[Notifications] Failed to mark as read:', err);
      }
    },
    []
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('[Notifications] Failed to mark all as read:', err);
    }
  }, []);

  // Handle category change
  const handleSetCategory = useCallback((newCategory: NotificationCategory) => {
    setCategory(newCategory);
    setOffset(0);
    setNotifications([]);
    setHasMore(true);
  }, []);

  // SSE connection management functions
  const connectSSE = useCallback(() => {
    if (!isAuthenticated || eventSourceRef.current) return;

    const eventSource = createNotificationEventSource(
      // On new notification, refresh data
      () => {
        console.log('[Notifications] New notification received via SSE');
        refresh();
      },
      // On error, schedule reconnect (only if tab is visible)
      () => {
        console.log('[Notifications] SSE error, will reconnect...');
        eventSourceRef.current = null;
        if (!document.hidden) {
          reconnectTimeoutRef.current = window.setTimeout(connectSSE, 3000);
        }
      },
      // On global merge request change, notify subscribers
      () => {
        console.log('[Notifications] Global merge request change via SSE');
        notifyGlobalMergeSubscribers();
      }
    );
    eventSourceRef.current = eventSource;
  }, [isAuthenticated, refresh, notifyGlobalMergeSubscribers]);

  const disconnectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('[Notifications] Disconnecting SSE (tab hidden)');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Manage SSE connection based on authentication and visibility
  // Consolidated into single useEffect to prevent race conditions
  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSSE();
      return;
    }

    if (isVisible) {
      // Tab is visible - connect if not already connected
      if (!eventSourceRef.current) {
        connectSSE();
        // If we were previously hidden, refresh to catch up on missed notifications
        if (wasHiddenRef.current) {
          console.log('[Notifications] Tab visible again, refreshing data...');
          refresh();
        }
      }
      wasHiddenRef.current = false;
    } else {
      // Tab is hidden - disconnect to save resources
      wasHiddenRef.current = true;
      disconnectSSE();
    }

    return () => {
      disconnectSSE();
    };
  }, [isAuthenticated, isVisible, connectSSE, disconnectSSE, refresh]);

  // Initial fetch when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      // Reset state when logged out
      setNotifications([]);
      setUnreadCount(0);
      setOffset(0);
      setHasMore(true);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when category changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(true);
    }
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        error,
        hasMore,
        category,
        refresh,
        loadMore,
        markAsRead,
        markAllAsRead,
        setCategory: handleSetCategory,
        subscribeToGlobalMergeChanges,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
